/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TCarouselItem } from "../../../viewModel/types";
import { Locale, LocaleKeys } from "../../../viewModel/locale";
import { interpolateLocaleTemplate } from "../../../viewModel/utility";
import { formatTilePreview } from "./TilePreviewFormatter";
import { TilePreviewTemplate } from "./TilePreviewTemplate";

/**
 * VoiceView: the spoken form of what a TilePreview shows for an item.
 *
 * Mirrors the field selection of each TilePreview template (see
 * TilePreviewFormatter), but phrased for the screen reader instead of laid
 * out for the eye: "Season 5, Episode 14" instead of "S5 :E14", "Start time
 * 6:00 PM, End time 7:30 PM" instead of "6:00-7:30pm", and "Channel HBO,
 * Channel number 200" instead of "HBO(200)". Parts are joined with ", " so
 * the reader pauses between them.
 */

const SEPARATOR: string = ", ";

const isPresent = (value: string | undefined | null): value is string =>
  value !== undefined && value !== null && value !== "";

const speakTime = (date: Date): string => {
  const hours24: number = date.getHours();
  const hours12: number = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const minutes: string = String(date.getMinutes()).padStart(2, "0");
  return `${hours12}:${minutes} ${hours24 < 12 ? "AM" : "PM"}`;
};

const speakDay = (date: Date): string => {
  const now: Date = new Date();
  const isToday: boolean =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  return isToday
    ? Locale.getString(LocaleKeys.HOME_TILE_PREVIEW_TODAY)
    : date.toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric"
      });
};

const parseDate = (value: string | undefined): Date | null => {
  if (!isPresent(value)) return null;
  const date: Date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "Season 5, Episode 14, Gray Matter" / "Gray Matter" / "" */
const speakEpisode = (item: TCarouselItem): string[] => {
  const parts: string[] = [];
  if (item.seasonNumber !== undefined && item.episodeNumber !== undefined) {
    parts.push(
      interpolateLocaleTemplate(
        Locale.getString(LocaleKeys.TALKBACK_SEASON_EPISODE),
        String(item.seasonNumber),
        String(item.episodeNumber)
      )
    );
  }
  if (isPresent(item.episodeTitle)) {
    parts.push(item.episodeTitle);
  }
  return parts;
};

const speakRating = (item: TCarouselItem): string[] =>
  isPresent(item.ratingBadge)
    ? [
        interpolateLocaleTemplate(
          Locale.getString(LocaleKeys.TALKBACK_RATING),
          item.ratingBadge
        )
      ]
    : [];

/** "Channel HBO, Channel number 200" — either half alone when only it is known. */
const speakChannel = (item: TCarouselItem): string[] => {
  const parts: string[] = [];
  if (isPresent(item.channelName)) {
    parts.push(
      interpolateLocaleTemplate(
        Locale.getString(LocaleKeys.TALKBACK_CHANNEL_NAME),
        item.channelName
      )
    );
  }
  if (isPresent(item.channelNumber)) {
    parts.push(
      interpolateLocaleTemplate(
        Locale.getString(LocaleKeys.TALKBACK_CHANNEL_NUMBER),
        item.channelNumber
      )
    );
  }
  return parts;
};

/** "Today, Start time 6:00 PM, End time 7:30 PM" — only with both times. */
const speakAiring = (item: TCarouselItem): string[] => {
  const start: Date | null = parseDate(item.startTime);
  const end: Date | null = parseDate(item.endTime);
  if (start === null || end === null) return [];
  return [
    speakDay(start),
    interpolateLocaleTemplate(
      Locale.getString(LocaleKeys.TALKBACK_START_TIME),
      speakTime(start)
    ),
    interpolateLocaleTemplate(
      Locale.getString(LocaleKeys.TALKBACK_END_TIME),
      speakTime(end)
    )
  ];
};

/** Movie line of the landscape templates: "2024, Rating TV-G". */
const speakMovieSubtitle = (item: TCarouselItem): string[] => [
  ...(item.movieYear !== undefined ? [String(item.movieYear)] : []),
  ...speakRating(item)
];

/** A formatter subtitle such as "Movie · 2010 · PG13", read as a list. */
const speakFormattedSubtitle = (
  item: TCarouselItem,
  template: TilePreviewTemplate
): string[] => {
  const subtitle: string | undefined = formatTilePreview(
    item,
    template
  ).subtitleInfo;
  return isPresent(subtitle) ? subtitle.split(" · ").filter(isPresent) : [];
};

const speakLandscape = (item: TCarouselItem): string[] =>
  item.contentType === "movie"
    ? [...speakMovieSubtitle(item), ...speakChannel(item), ...speakAiring(item)]
    : [
        ...speakEpisode(item),
        ...speakRating(item),
        ...speakChannel(item),
        ...speakAiring(item)
      ];

/**
 * Builds the screen-reader text for an item's preview, title first.
 *
 * @param item - The carousel item (with channel name/number already
 *   resolved where the visible preview resolves them).
 * @param template - The TilePreview template the screen shows for it.
 */
export const buildTilePreviewSpeech = (
  item: TCarouselItem,
  template: TilePreviewTemplate
): string => {
  let details: string[];
  switch (template) {
    case TilePreviewTemplate.LOGO_PIP_LANDSCAPE_PREVIEW:
      details = speakEpisode(item);
      break;
    case TilePreviewTemplate.LIVE_PIP_LANDSCAPE_PREVIEW:
      details = speakChannel(item);
      break;
    case TilePreviewTemplate.MOVIE_POSTER_HORIZONTAL:
    case TilePreviewTemplate.SHOW_POSTER_HORIZONTAL:
    case TilePreviewTemplate.SERIES_RECORDING_HORIZONTAL:
    case TilePreviewTemplate.MOVIE_RECORDING_HORIZONTAL:
      details = speakFormattedSubtitle(item, template);
      break;
    default:
      details = speakLandscape(item);
  }
  return [item.title, ...details].filter(isPresent).join(SEPARATOR);
};
